import { useMemo } from 'react';
import { useUser } from '@/hooks/useUser';
import { UserRole, RoleInfo } from '@/types';
import { getUserRole } from '@/types/auth';

// Permission constants matching backend
export const PERMISSIONS = {
  // User data permissions
  READ_OWN_DATA: 'read_own_data',
  WRITE_OWN_DATA: 'write_own_data',
  DELETE_OWN_DATA: 'delete_own_data',
  
  // Control type management
  VIEW_CONTROL_TYPES: 'view_control_types',
  CREATE_CONTROL_TYPES: 'create_control_types',
  EDIT_CONTROL_TYPES: 'edit_control_types',
  DELETE_CONTROL_TYPES: 'delete_control_types',
  MANAGE_CONTROL_VERSIONS: 'manage_control_versions',
  
  // System administration
  VIEW_SYSTEM_STATS: 'view_system_stats',
  VIEW_AUDIT_LOGS: 'view_audit_logs',
  MANAGE_SYSTEM_CONFIG: 'manage_system_config',
  VIEW_PERFORMANCE_METRICS: 'view_performance_metrics',
  
  // User management (Super Admin only)
  VIEW_ALL_USERS: 'view_all_users',
  MANAGE_USERS: 'manage_users',
  ASSIGN_ROLES: 'assign_roles',
  
  // Data management
  VIEW_ALL_DATA: 'view_all_data',
  EXPORT_DATA: 'export_data',
  IMPORT_DATA: 'import_data',
} as const;

export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS];

// Role hierarchy and permission mapping (matches backend)
const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  [UserRole.AGROKOLL_OWNER]: [
    PERMISSIONS.READ_OWN_DATA,
    PERMISSIONS.WRITE_OWN_DATA,
    PERMISSIONS.DELETE_OWN_DATA,
  ],

  [UserRole.AGROKOLL_ADMIN]: [
    PERMISSIONS.READ_OWN_DATA,
    PERMISSIONS.WRITE_OWN_DATA,
    PERMISSIONS.DELETE_OWN_DATA,
  ],

  [UserRole.AGROKOLL_RADGIVARE]: [
    PERMISSIONS.READ_OWN_DATA,
    PERMISSIONS.WRITE_OWN_DATA,
    PERMISSIONS.DELETE_OWN_DATA,
  ],

  [UserRole.AGROKOLL_B2B]: [
    PERMISSIONS.READ_OWN_DATA,
    PERMISSIONS.WRITE_OWN_DATA,
    PERMISSIONS.DELETE_OWN_DATA,
    PERMISSIONS.VIEW_CONTROL_TYPES,
    PERMISSIONS.CREATE_CONTROL_TYPES,
    PERMISSIONS.EDIT_CONTROL_TYPES,
    PERMISSIONS.DELETE_CONTROL_TYPES,
    PERMISSIONS.MANAGE_CONTROL_VERSIONS,
    PERMISSIONS.VIEW_SYSTEM_STATS,
  ],

  [UserRole.AGROKOLL_SYSTEM_ADMIN]: [
    PERMISSIONS.READ_OWN_DATA,
    PERMISSIONS.WRITE_OWN_DATA,
    PERMISSIONS.DELETE_OWN_DATA,
    PERMISSIONS.VIEW_CONTROL_TYPES,
    PERMISSIONS.CREATE_CONTROL_TYPES,
    PERMISSIONS.EDIT_CONTROL_TYPES,
    PERMISSIONS.DELETE_CONTROL_TYPES,
    PERMISSIONS.MANAGE_CONTROL_VERSIONS,
    PERMISSIONS.VIEW_SYSTEM_STATS,
    PERMISSIONS.VIEW_AUDIT_LOGS,
    PERMISSIONS.MANAGE_SYSTEM_CONFIG,
    PERMISSIONS.VIEW_PERFORMANCE_METRICS,
    PERMISSIONS.VIEW_ALL_DATA,
    PERMISSIONS.EXPORT_DATA,
    PERMISSIONS.IMPORT_DATA,
    PERMISSIONS.VIEW_ALL_USERS,
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.ASSIGN_ROLES,
  ],
};

/**
 * Hook for checking user permissions and roles
 *
 * Uses the roles array from /auth/me response (UserProfileDto)
 * Returns permissions based on the highest role in the hierarchy
 */
export const usePermissions = (): RoleInfo => {
  const { user, isLoading } = useUser();

  return useMemo(() => {
    if (isLoading || !user) {
      return {
        role: null,
        permissions: [],
        hasPermission: () => false,
        hasRole: () => false,
      };
    }

    // Get highest role from user's roles array
    const role = getUserRole(user);
    const userPermissions = role ? ROLE_PERMISSIONS[role] || [] : [];

    return {
      role,
      permissions: userPermissions,
      hasPermission: (permission: string) => userPermissions.includes(permission as Permission),
      hasRole: (checkRole: UserRole) => user.roles?.includes(checkRole) || false,
    };
  }, [user, isLoading]);
};

/**
 * Check if user has any of the specified roles
 */
export const useHasAnyRole = (roles: UserRole[]): boolean => {
  const { role } = usePermissions();
  return role !== null && roles.includes(role);
};

/**
 * Check if user has any of the specified permissions
 */
export const useHasAnyPermission = (permissions: Permission[]): boolean => {
  const { hasPermission } = usePermissions();
  return permissions.some(permission => hasPermission(permission));
};

/**
 * Check if user is admin (B2B or System Admin)
 */
export const useIsAdmin = (): boolean => {
  return useHasAnyRole([UserRole.AGROKOLL_B2B, UserRole.AGROKOLL_SYSTEM_ADMIN]);
};

/**
 * Check if user is system admin
 */
export const useIsSystemAdmin = (): boolean => {
  return useHasAnyRole([UserRole.AGROKOLL_SYSTEM_ADMIN]);
};